import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import LogoIcon from "./LogoIcon.tsx";
import { formatMessageContent } from "./formatMessage.ts";
import {
  askSupport,
  currentUserEmail,
  submitSupportTicket,
  supportChatErrorMessage,
  ticketErrorMessage,
  type SupportTurn,
} from "./supportApi.ts";
import {
  loadSupportHistory,
  saveSupportHistory,
  type SupportMessage,
} from "./supportHistory.ts";

export interface SupportChatProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Which app the coach is in — sent with a ticket so support has context.
   * Asked when the ticket is sent: the chat stays mounted while the coach
   * moves between apps.
   */
  getAppKey?: () => string | undefined;
}

/**
 * The support assistant, in the same panel ModernAI uses in reports.
 *
 * Deliberately chat-only: ModernAI opens on a grid of preset prompts because
 * its questions are analytical and hard to phrase, but a coach coming here
 * already knows what went wrong and just wants to type it (client 2026-09).
 */

/**
 * The assistant's reply, rendered once per content string.
 *
 * formatMessageContent runs the whole markdown-to-HTML pass; memoising on the
 * text means editing or appending a message does not re-render every earlier
 * bubble's HTML. Same renderer ModernAI uses, so a bolded menu path reads as
 * bold rather than as asterisks.
 */
const AssistantMarkdown = memo(function AssistantMarkdown({ content }: { content: string }) {
  const html = useMemo(() => formatMessageContent(content), [content]);
  return (
    <div
      className="prose prose-sm max-w-none text-sm leading-6 text-gray-900"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
});

export function SupportChat({ open, onOpenChange, getAppKey }: SupportChatProps) {
  // Restored on first render, so reopening the panel shows the last answers
  // rather than an empty box.
  const [messages, setMessages] = useState<SupportMessage[]>(() => loadSupportHistory());
  const [question, setQuestion] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [isTicketOpen, setTicketOpen] = useState(false);
  // Set when the coach says they are done, so the follow-up stops asking.
  const [isWrappedUp, setWrappedUp] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    saveSupportHistory(messages);
  }, [messages]);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  /**
   * The newest answer. Only it offers follow-ups: repeating "anything else?"
   * under every past answer would stack a column of dead prompts.
   */
  const lastAnswerIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i]?.role === "assistant") return i;
    }
    return -1;
  }, [messages]);

  /** History for the model and the ticket: real turns only — no pending
   * placeholder, no client-side error notice. */
  const history = useMemo(
    (): SupportTurn[] =>
      messages
        .filter((m) => !m.pending && !m.transient)
        .map(({ role, content }) => ({ role, content })),
    [messages]
  );

  const send = useCallback(
    async (text: string) => {
      const asked = text.trim();
      if (!asked || isSending) return;

      const priorHistory = history;
      setQuestion("");
      setIsSending(true);
      setWrappedUp(false);
      setMessages((prev) => [
        ...prev,
        { role: "user", content: asked },
        { role: "assistant", content: "", pending: true },
      ]);

      try {
        const answer = await askSupport(asked, priorHistory);
        setMessages((prev) => [
          ...prev.slice(0, -1),
          {
            role: "assistant",
            content: answer.reply,
            suggestions: answer.suggestions,
            unresolved: !answer.answered,
            video: answer.video,
          },
        ]);
      } catch (error) {
        // The question was cleared optimistically above; put it back so a
        // coach on a flaky sideline can retry with one tap instead of
        // retyping — unless they have already started a new one.
        setQuestion((cur) => (cur.trim() ? cur : asked));
        // A client-side notice, not a real answer: transient so it is
        // never persisted, replayed to the model, or sent with a ticket.
        setMessages((prev) => [
          ...prev.slice(0, -1),
          {
            role: "assistant",
            content: supportChatErrorMessage(error),
            unresolved: true,
            transient: true,
          },
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [history, isSending]
  );

  if (!open) return null;

  const sendDisabled = isSending || !question.trim();

  return (
    <div className="pointer-events-none fixed inset-0 z-40">
      <div
        className="pointer-events-auto absolute inset-0 bg-black/25 backdrop-blur-[1.25px]"
        onClick={() => onOpenChange(false)}
      />
      <div
        onClick={(event) => event.stopPropagation()}
        className="pointer-events-auto fixed bottom-6 right-6 flex h-[760px] max-h-[92vh] w-[520px] max-w-[92vw] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-3">
          <div className="flex items-center gap-2 text-base font-semibold text-gray-900">
            <LogoIcon size={22} className="text-blue-600" />
            <span>Chat Support</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onOpenChange(false)}
              className="flex h-9 w-9 items-center justify-center text-2xl font-semibold text-gray-500 transition hover:text-gray-900"
              aria-label="Close Chat Support"
            >
              ×
            </button>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col bg-gray-50 px-5 pb-4 pt-4">
          <div className="flex min-h-0 flex-1 flex-col rounded-[20px] bg-white px-5 py-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
            {/* A log: screen readers hear each new answer as it lands. */}
            <div
              role="log"
              aria-live="polite"
              className="min-h-0 flex-1 space-y-4 overflow-y-auto pr-1"
            >
              {messages.length === 0 ? (
                <div className="flex h-full items-center justify-center px-4 text-center text-sm leading-6 text-gray-500">
                  Ask how to do something in Modern Football. If I can&rsquo;t answer it, you can
                  send it to the support team.
                </div>
              ) : (
                messages.map((message, index) => {
                  const isUser = message.role === "user";
                  return (
                    <div key={index} className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
                      {isUser ? (
                        <span className="inline-flex max-w-[80%] items-center rounded-full bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 shadow-sm">
                          <span className="whitespace-pre-wrap">{message.content}</span>
                        </span>
                      ) : (
                        <div className="w-full space-y-3">
                          {message.pending ? (
                            <span className="animate-pulse text-sm text-gray-700">
                              Looking that up...
                            </span>
                          ) : (
                            <AssistantMarkdown content={message.content} />
                          )}

                          {message.video ? (
                            <figure className="w-full overflow-hidden rounded-xl border border-gray-200 bg-black sm:w-1/2">
                              {/* Not autoplayed: a coach may be on a
                                  sideline with the sound up. */}
                              <video
                                src={message.video.url}
                                controls
                                preload="metadata"
                                playsInline
                                className="h-auto w-full"
                                onLoadedMetadata={(event) => {
                                  const el = event.currentTarget;
                                  // Show the FIRST FRAME as the thumbnail instead of a black box:
                                  // nudge the playhead so a frame decodes (Safari/iOS render nothing
                                  // until one does — client 2026-09). Harmless if seeking is blocked.
                                  if (el.currentTime === 0) {
                                    try {
                                      el.currentTime = 0.1;
                                    } catch {
                                      /* seeking not supported — leave the default frame */
                                    }
                                  }
                                }}
                              />
                              {message.video.question ? (
                                <figcaption className="bg-white px-3 py-2 text-xs text-gray-600">
                                  {message.video.question}
                                </figcaption>
                              ) : null}
                            </figure>
                          ) : null}

                          {/* Follow-up, on the newest answer only. */}
                          {index === lastAnswerIndex && !message.pending ? (
                            isWrappedUp ? (
                              <p className="text-sm text-gray-500">
                                Glad that helped. Open Chat Support any time.
                              </p>
                            ) : (
                              <div className="space-y-3">
                                <p className="text-sm text-gray-600">
                                  {message.unresolved && message.suggestions?.length
                                    ? "Was it one of these instead?"
                                    : "Can I help you with something else?"}
                                </p>

                                {message.suggestions?.length ? (
                                  <div className="grid gap-2">
                                    {message.suggestions.map((suggestion, suggestionIndex) => (
                                      <button
                                        key={`${suggestionIndex}-${suggestion}`}
                                        type="button"
                                        onClick={() => void send(suggestion)}
                                        className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-800 transition hover:bg-gray-50"
                                      >
                                        {suggestion}
                                      </button>
                                    ))}
                                  </div>
                                ) : null}

                                <div className="flex flex-wrap items-center gap-2">
                                  {message.unresolved ? (
                                    <button
                                      type="button"
                                      onClick={() => setTicketOpen(true)}
                                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
                                    >
                                      Contact support
                                    </button>
                                  ) : null}
                                  <button
                                    type="button"
                                    onClick={() => setWrappedUp(true)}
                                    className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50"
                                  >
                                    No, that&rsquo;s it
                                  </button>
                                </div>
                              </div>
                            )
                          ) : null}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Same ask box as ModernAI. */}
            <div className="relative mt-4 flex flex-shrink-0 items-center gap-3 rounded-2xl border border-gray-200 bg-white px-4 py-1">
              {!question.trim() && (
                <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-sm text-gray-500">
                  Ask Anything
                </span>
              )}
              <textarea
                value={question}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void send(question);
                  }
                }}
                aria-label="Ask Anything"
                enterKeyHint="send"
                // 16px font prevents iOS Safari's auto-zoom-on-focus, which
                // forces an expensive viewport resize on iPad.
                style={{ fontSize: 16 }}
                className="flex min-h-[68px] w-full resize-none border-0 bg-transparent px-0 py-[12px] leading-5 text-gray-900 focus:outline-none focus:ring-0 focus-visible:ring-0"
                rows={2}
                maxLength={2000}
              />
              <button
                onClick={() => void send(question)}
                disabled={sendDisabled}
                className={`flex aspect-square h-12 w-12 flex-shrink-0 items-center justify-center rounded-full border-2 p-0 transition-colors ${
                  sendDisabled
                    ? "border-gray-300 bg-white text-gray-400"
                    : "border-blue-600 bg-white text-blue-600 hover:bg-blue-50"
                }`}
                aria-label="Send"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="12" y1="19" x2="12" y2="5" />
                  <polyline points="5 12 12 5 19 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <SupportTicketDialog
        open={isTicketOpen}
        onOpenChange={setTicketOpen}
        transcript={history.slice(-20)}
        getAppKey={getAppKey}
      />
    </div>
  );
}

/**
 * The ticket form.
 *
 * The chat so far rides along with it, so support can see what the coach
 * already tried instead of asking them to repeat it.
 */
function SupportTicketDialog({
  open,
  onOpenChange,
  transcript,
  getAppKey,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  transcript: SupportTurn[];
  getAppKey?: () => string | undefined;
}) {
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [isSending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSent, setSent] = useState(false);

  // Prefill on each open, and clear the previous ticket so the form is
  // reusable. Subject and message are cleared too, or a coach raising a
  // SECOND ticket would find the first one's text still sitting in the form
  // (the seeding below only fills a blank field).
  useEffect(() => {
    if (!open) return;
    setEmail(currentUserEmail());
    setSubject("");
    setMessage("");
    setError(null);
    setSent(false);
  }, [open]);

  // Seed from the last thing the coach asked — usually exactly what they
  // want help with, so the form opens mostly filled in.
  useEffect(() => {
    if (!open) return;
    const lastQuestion = [...transcript].reverse().find((turn) => turn.role === "user")?.content;
    if (lastQuestion) {
      setSubject((prev) => prev || lastQuestion.slice(0, 120));
      setMessage((prev) => prev || lastQuestion);
    }
  }, [open, transcript]);

  if (!open) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (isSending) return;
    setSending(true);
    setError(null);
    try {
      await submitSupportTicket({
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
        transcript,
        appKey: getAppKey?.(),
      });
      setSent(true);
    } catch (error) {
      setError(ticketErrorMessage(error, "Your ticket"));
    } finally {
      setSending(false);
    }
  };

  const canSubmit = Boolean(email.trim()) && Boolean(subject.trim()) && Boolean(message.trim());

  return (
    <div className="pointer-events-auto fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4">
      <div className="w-[min(560px,96vw)] rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Contact support</h3>
            <p className="mt-1 text-sm text-gray-600">The team will reply to your email address.</p>
          </div>
          <button
            type="button"
            aria-label="Close ticket form"
            onClick={() => onOpenChange(false)}
            className="rounded-full px-2 text-2xl font-semibold text-gray-500 transition hover:text-gray-900"
          >
            ×
          </button>
        </div>

        {isSent ? (
          <div className="mt-6 space-y-4">
            <p className="text-sm text-gray-800">
              Your ticket is on its way. Support will reply to <strong>{email}</strong>.
            </p>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700"
            >
              Done
            </button>
          </div>
        ) : (
          <form className="mt-5 space-y-4" onSubmit={submit}>
            <label className="block">
              <span className="text-sm font-medium text-gray-800">Your email</span>
              <input
                type="email"
                required
                maxLength={320}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-800">Subject</span>
              <input
                required
                maxLength={200}
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </label>
            <label className="block">
              <span className="text-sm font-medium text-gray-800">How can we help?</span>
              <textarea
                required
                rows={5}
                maxLength={5000}
                value={message}
                onChange={(event) => setMessage(event.target.value)}
                className="mt-1 w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
              />
            </label>

            {transcript.length > 0 && (
              <p className="text-xs text-gray-500">
                Your chat so far will be included so support has the context.
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!canSubmit || isSending}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:opacity-40"
              >
                {isSending ? "Sending..." : "Send ticket"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
